'use client';

import { useId } from 'react';

import { ROUTES, SMS_MARKETING_DISCLOSURE } from '@repo/common';
import { DataTestIds } from '@repo/frontend-shared';
import { SDSCheckbox } from '@repo/sds/SDSCheckbox';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSLink } from '@repo/sds/SDSLink';
import type { SDSTextType } from '@repo/sds/SDSText';
import { SDSText } from '@repo/sds/SDSText';

const ids = DataTestIds.SMS_MARKETING;

interface SmsConsentCheckboxProps {
  isChecked: boolean;
  isDisabled?: boolean;
  onChange: (checked: boolean) => void;
  labelType?: SDSTextType;
  className?: string;
}

/** The explicit SMS consent box with the versioned wording; it never starts checked. */
export function SmsConsentCheckbox({
  isChecked,
  isDisabled,
  onChange,
  labelType = 'body-medium',
  className,
}: SmsConsentCheckboxProps) {
  const consentId = useId();
  const consentLabelId = useId();
  return (
    <SDSFlexbox direction="row" gap="text-medium" alignItems="flex-start" className={className}>
      <SDSCheckbox
        id={consentId}
        isChecked={isChecked}
        isDisabled={isDisabled}
        ariaLabelledBy={consentLabelId}
        onChange={onChange}
        dataTestId={ids.CONSENT_CHECKBOX}
      />
      <label htmlFor={consentId} id={consentLabelId}>
        <SDSText type={labelType} color="text-secondary" text={SMS_MARKETING_DISCLOSURE.consentLabel} />
      </label>
    </SDSFlexbox>
  );
}

export function SmsNotAConditionNotice() {
  return <SDSText type="body-small" color="muted" text={SMS_MARKETING_DISCLOSURE.notAConditionNotice} />;
}

export function SmsPrivacyNotice() {
  const { privacyNotice, policyLinks } = SMS_MARKETING_DISCLOSURE;
  return (
    <SDSText
      type="body-small"
      color="muted"
      text={
        <>
          {privacyNotice} {policyLinks.prefix}{' '}
          <SDSLink href={ROUTES.PRIVACY_POLICY} target="_blank" dataTestId={ids.PRIVACY_POLICY_LINK}>
            {policyLinks.privacyPolicy}
          </SDSLink>{' '}
          {policyLinks.conjunction}{' '}
          <SDSLink href={ROUTES.TERMS_OF_USE} target="_blank" dataTestId={ids.TERMS_OF_USE_LINK}>
            {policyLinks.termsOfUse}
          </SDSLink>
          .
        </>
      }
    />
  );
}
